import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useFetchRegulationDocument } from 'components/Dashboard/Regulations/useRegulationDocumentData';

const buildDocketCrumb = (docketId, docketTitle) =>
  docketId && docketTitle
    ? { label: docketTitle, to: `/regulation-docket/${docketId}/${normalize(docketTitle)}` }
    : null;

const buildDocumentCrumb = (documentId, documentTitle) =>
  documentId && documentTitle
    ? {
        label: documentTitle,
        to: `/regulation-document/${documentId}/${normalize(documentTitle)}#comments`,
      }
    : null;

const BreadcrumbCrumb = ({ crumb, isLast }) => {
  const crumbRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const isLink = !!crumb.to && !isLast;

  useLayoutEffect(() => {
    const element = crumbRef.current;
    if (!element) return undefined;

    const updateIsTruncated = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth + 1);
    };

    updateIsTruncated();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateIsTruncated);
      return () => window.removeEventListener('resize', updateIsTruncated);
    }

    const resizeObserver = new ResizeObserver(updateIsTruncated);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [crumb.label]);

  const tooltipProps = isTruncated
    ? {
        'data-tooltip-content': crumb.label,
        'data-tooltip-id': 'general-tooltip',
        'data-tooltip-class-name': 'header-tooltip pricing-feature-tooltip',
      }
    : {};

  const className = `${
    isLink ? 'text-light-blue link-hover' : isLast ? 'text-main-blue' : 'text-main-blue-65'
  } paragraph-p1 nowrap-item regulations-breadcrumb-crumb`;

  if (isLink) {
    return (
      <Link to={crumb.to} className={className} ref={crumbRef} {...tooltipProps}>
        {crumb.label}
      </Link>
    );
  }

  return (
    <span className={className} ref={crumbRef} {...tooltipProps}>
      {crumb.label}
    </span>
  );
};

const RegulationsBreadcrumbs = ({ item }) => {
  const { fetchRegulationDocument } = useFetchRegulationDocument();
  const [fetchedDocument, setFetchedDocument] = useState(null);

  const documentId = item?.regulationDocumentId;
  const documentTitleFromItem = item?.regulationDocumentTitle ?? item?.documentTitle;
  const shouldFetchDocument = !!documentId && !documentTitleFromItem;

  useEffect(() => {
    if (!shouldFetchDocument) {
      setFetchedDocument(null);
      return undefined;
    }
    const source = new AbortController();
    (async () => {
      const document = await fetchRegulationDocument({ id: documentId, source });
      if (!source.signal.aborted) {
        setFetchedDocument(document);
      }
    })();
    return () => source.abort('Breadcrumb document fetch cancelled.');
  }, [shouldFetchDocument, documentId]);

  if (!item) return null;

  const docketId = item.regulationDocketId ?? fetchedDocument?.regulationDocketId;
  const docketTitle = item.docketTitle ?? fetchedDocument?.docketTitle;
  const documentTitle = documentTitleFromItem ?? fetchedDocument?.title;

  const docketCrumb = buildDocketCrumb(docketId, docketTitle);
  const documentCrumb = buildDocumentCrumb(documentId, documentTitle);
  const currentCrumb = item.title ? { label: item.title } : null;

  const items = [docketCrumb, documentCrumb, currentCrumb].filter(Boolean);
  if (items.length === 0) return null;

  return (
    <nav aria-label='Breadcrumb' className='regulations-breadcrumbs paragraph-p3 d-flex align-items-center mb-2'>
      {items.map((crumb, index) => {
        const isLast = index === items.length - 1;
        return (
          <React.Fragment key={`${crumb.label}-${index}`}>
            {index > 0 && (
              <span className='mx-2 paragraph-p2 icon-arrow-next text-main-blue-65 nowrap-item' aria-hidden='true' />
            )}
            <BreadcrumbCrumb crumb={crumb} isLast={isLast} />
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default RegulationsBreadcrumbs;
